import { useEffect, useState } from 'react'
import userService, {
  UserAdmin,
  RawProjectPermissionResponseItem,
  LabelProjectPermissionResponseItem,
  DatasetProjectPermissionResponseItem,
  TestDatasetProjectPermissionResponseItem,
} from '../services/userService'
import { PERMISSIONS } from '../constants/permissions'

interface Props {
  isOpen: boolean
  user: UserAdmin | null
  onClose: () => void
  onUpdated: (user: UserAdmin) => void
}

const GLOBAL_PERMISSION_GROUPS: { label: string; items: { code: string; label: string }[] }[] = [
  {
    label: '페이지 접근',
    items: [
      { code: PERMISSIONS.PAGE_DASHBOARD, label: '대시보드' },
      { code: PERMISSIONS.PAGE_RAW_DATA, label: '원천 데이터 관리' },
      { code: PERMISSIONS.PAGE_IMAGE_EXPLORER, label: '이미지 탐색' },
      { code: PERMISSIONS.PAGE_LABEL_DATA, label: '라벨 데이터 관리' },
      { code: PERMISSIONS.PAGE_DATASET, label: '학습 데이터셋 관리' },
      { code: PERMISSIONS.PAGE_TEST_DATASET, label: '평가 데이터셋 관리' },
      { code: PERMISSIONS.PAGE_TRAINING, label: '학습 이력 관리' },
      { code: PERMISSIONS.PAGE_DEPLOYMENT, label: '모델 배포 관리' },
      { code: PERMISSIONS.PAGE_USER_MANAGEMENT, label: '사용자 관리' },
      { code: PERMISSIONS.PAGE_RAW_COLLECTION_REPORT, label: '수집 리포트' },
    ],
  },
  {
    label: '원천 데이터 탭',
    items: [
      { code: PERMISSIONS.RAW_DATA_MANUAL_TAB, label: '수동 업로드 탭' },
      { code: PERMISSIONS.RAW_DATA_AUTO_TAB, label: '자동 업로드 탭' },
    ],
  },
  {
    label: '라벨 데이터',
    items: [
      { code: PERMISSIONS.LABEL_PROJECT_MANAGE, label: '라벨 프로젝트/구조 생성' },
    ],
  },
  {
    label: '학습 데이터셋',
    items: [
      { code: PERMISSIONS.DATASET_PROJECT_MANAGE, label: '학습 데이터셋 프로젝트/구조 생성' },
    ],
  },
]

export default function EditUserPermissionsModal({ isOpen, user, onClose, onUpdated }: Props) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<'global' | 'raw' | 'label' | 'dataset' | 'test-dataset'>('global')
  const [allPermissions, setAllPermissions] = useState<string[]>([])
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set())
  const [projectPerms, setProjectPerms] = useState<RawProjectPermissionResponseItem[]>([])
  const [labelProjectPerms, setLabelProjectPerms] = useState<LabelProjectPermissionResponseItem[]>([])
  const [datasetProjectPerms, setDatasetProjectPerms] = useState<DatasetProjectPermissionResponseItem[]>([])
  const [projectFilter, setProjectFilter] = useState('')
  const [projectUploadModeFilter, setProjectUploadModeFilter] = useState<'all' | 'manual' | 'auto'>('all')
  const [rawShowAssignedOnly, setRawShowAssignedOnly] = useState<boolean>(false)
  const [labelProjectFilter, setLabelProjectFilter] = useState('')
  const [labelShowAssignedOnly, setLabelShowAssignedOnly] = useState<boolean>(false)
  const [datasetProjectFilter, setDatasetProjectFilter] = useState('')
  const [datasetShowAssignedOnly, setDatasetShowAssignedOnly] = useState<boolean>(false)
  const [testDatasetProjectPerms, setTestDatasetProjectPerms] = useState<TestDatasetProjectPermissionResponseItem[]>([])
  const [testDatasetProjectFilter, setTestDatasetProjectFilter] = useState('')
  const [testDatasetShowAssignedOnly, setTestDatasetShowAssignedOnly] = useState<boolean>(false)

  useEffect(() => {
    if (!isOpen || !user) return
    ;(async () => {
      try {
        setLoading(true)
        const [available, rawProjects, labelProjects, datasetProjects, testDatasetProjects] = await Promise.all([
          userService.listAvailablePermissions(),
          userService.getRawProjectPermissions(user.id),
          userService.getLabelProjectPermissions(user.id),
          userService.getDatasetProjectPermissions(user.id),
          userService.getTestDatasetProjectPermissions(user.id),
        ])
        setAllPermissions(available)
        setSelectedPerms(new Set(user.permissions || []))
        setProjectPerms(rawProjects)
        setLabelProjectPerms(labelProjects)
        setProjectUploadModeFilter('all')
        setRawShowAssignedOnly(false)
        setLabelProjectFilter('')
        setLabelShowAssignedOnly(false)
        setDatasetProjectPerms(datasetProjects)
        setDatasetProjectFilter('')
        setDatasetShowAssignedOnly(false)
        setTestDatasetProjectPerms(testDatasetProjects)
        setTestDatasetProjectFilter('')
        setTestDatasetShowAssignedOnly(false)
        setActiveTab('global')
      } catch (e: any) {
        alert(e?.message || '권한 정보를 불러오지 못했습니다.')
      } finally {
        setLoading(false)
      }
    })()
  }, [isOpen, user])

  if (!isOpen || !user) return null

  const isSuper = user.permissions.includes('admin')

  const togglePerm = (code: string) => {
    setSelectedPerms(prev => {
      const ns = new Set(prev)
      if (ns.has(code)) ns.delete(code)
      else ns.add(code)
      return ns
    })
  }

  // === 리소스 권한 토글 유틸 ===

  const isRawActionAllowed = (p: RawProjectPermissionResponseItem, action: string) => {
    const mode = String(p.upload_mode || '').toLowerCase()
    if (mode === 'auto') {
      return action === 'view' || action === 'download'
    }
    const allowed = new Set(['view', 'manual_upload', 'download', 'delete', 'create'])
    return allowed.has(action)
  }

  const toggleRawProjectAction = (projectId: number, action: string) => {
    setProjectPerms(prev =>
      prev.map(p => {
        if (p.project_id !== projectId || !isRawActionAllowed(p, action)) return p
        const current = p.actions || {}
        return {
          ...p,
          actions: { ...current, [action]: !current[action] },
        }
      }),
    )
  }

  const toggleRawColumnAll = (action: string, rows: RawProjectPermissionResponseItem[]) => {
    if (rows.length === 0) return
    const applicable = rows.filter(p => isRawActionAllowed(p, action))
    if (applicable.length === 0) return
    const allOn = applicable.every(p => !!(p.actions || {})[action])
    setProjectPerms(prev =>
      prev.map(p => {
        const target = applicable.find(r => r.project_id === p.project_id)
        if (!target) return p
        const current = p.actions || {}
        return {
          ...p,
          actions: { ...current, [action]: !allOn },
        }
      }),
    )
  }

  const isLabelActionAllowed = (_p: LabelProjectPermissionResponseItem, _action: string) => true

  const toggleLabelProjectAction = (projectId: number, action: string) => {
    setLabelProjectPerms(prev =>
      prev.map(p => {
        if (p.project_id !== projectId || !isLabelActionAllowed(p, action)) return p
        const current = p.actions || {}
        return {
          ...p,
          actions: { ...current, [action]: !current[action] },
        }
      }),
    )
  }

  const toggleLabelColumnAll = (action: string, rows: LabelProjectPermissionResponseItem[]) => {
    if (rows.length === 0) return
    const allOn = rows.every(p => !!(p.actions || {})[action])
    setLabelProjectPerms(prev =>
      prev.map(p => {
        const target = rows.find(r => r.project_id === p.project_id)
        if (!target) return p
        const current = p.actions || {}
        return {
          ...p,
          actions: { ...current, [action]: !allOn },
        }
      }),
    )
  }

  const toggleTestDatasetProjectAction = (projectId: number, action: string) => {
    setTestDatasetProjectPerms(prev =>
      prev.map(p => {
        if (p.project_id !== projectId) return p
        const current = p.actions || {}
        return { ...p, actions: { ...current, [action]: !current[action] } }
      }),
    )
  }

  const toggleTestDatasetColumnAll = (action: string, rows: TestDatasetProjectPermissionResponseItem[]) => {
    if (rows.length === 0) return
    const allOn = rows.every(p => !!(p.actions || {})[action])
    setTestDatasetProjectPerms(prev =>
      prev.map(p => {
        const target = rows.find(r => r.project_id === p.project_id)
        if (!target) return p
        const current = p.actions || {}
        return { ...p, actions: { ...current, [action]: !allOn } }
      }),
    )
  }

  const isDatasetActionAllowed = (_p: DatasetProjectPermissionResponseItem, _action: string) => true

  const toggleDatasetProjectAction = (projectId: number, action: string) => {
    setDatasetProjectPerms(prev =>
      prev.map(p => {
        if (p.project_id !== projectId || !isDatasetActionAllowed(p, action)) return p
        const current = p.actions || {}
        return {
          ...p,
          actions: { ...current, [action]: !current[action] },
        }
      }),
    )
  }

  const toggleDatasetColumnAll = (action: string, rows: DatasetProjectPermissionResponseItem[]) => {
    if (rows.length === 0) return
    const allOn = rows.every(p => !!(p.actions || {})[action])
    setDatasetProjectPerms(prev =>
      prev.map(p => {
        const target = rows.find(r => r.project_id === p.project_id)
        if (!target) return p
        const current = p.actions || {}
        return {
          ...p,
          actions: { ...current, [action]: !allOn },
        }
      }),
    )
  }

  const handleSave = async () => {
    if (!user) return
    try {
      setSaving(true)
      let updatedUser = user

      // 1) 전역 권한 저장 (superuser 인 경우 서버에서 거부되므로 표시만)
      if (!isSuper) {
        const names = Array.from(selectedPerms)
        updatedUser = await userService.setUserPermissions(user.id, names)
      }

      // 2) 리소스 단위 권한 저장
      if (!isSuper) {
        const rawItems = projectPerms.map(p => ({
          project_id: p.project_id,
          actions: p.actions || {},
        }))
        const labelItems = labelProjectPerms.map(p => ({
          project_id: p.project_id,
          actions: p.actions || {},
        }))
        const datasetItems = datasetProjectPerms.map(p => ({
          project_id: p.project_id,
          actions: p.actions || {},
        }))
        const testDatasetItems = testDatasetProjectPerms.map(p => ({
          project_id: p.project_id,
          actions: p.actions || {},
        }))

        await Promise.all([
          userService.setRawProjectPermissions(user.id, rawItems),
          userService.setLabelProjectPermissions(user.id, labelItems),
          userService.setDatasetProjectPermissions(user.id, datasetItems),
          userService.setTestDatasetProjectPermissions(user.id, testDatasetItems),
        ])
      }

      onUpdated(updatedUser)
      onClose()
    } catch (e: any) {
      alert(e?.message || '권한 저장에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const normalizedFilter = projectFilter.trim().toLowerCase()
  const filteredRawProjects = projectPerms
    .filter(p => {
      if (!normalizedFilter) return true
      try {
        return p.project_name.toLowerCase().includes(normalizedFilter)
      } catch {
        return true
      }
    })
    .filter(p => {
      if (projectUploadModeFilter === 'all') return true
      if (projectUploadModeFilter === 'auto') return p.upload_mode === 'auto'
      return p.upload_mode !== 'auto'
    })
    .filter(p => {
      if (!rawShowAssignedOnly) return true
      const actions = p.actions || {}
      return Object.values(actions).some(Boolean)
    })

  const normalizedLabelFilter = labelProjectFilter.trim().toLowerCase()
  const filteredLabelProjects = labelProjectPerms
    .filter(p => {
      if (!normalizedLabelFilter) return true
      try {
        return p.project_name.toLowerCase().includes(normalizedLabelFilter)
      } catch {
        return true
      }
    })
    .filter(p => {
      if (!labelShowAssignedOnly) return true
      const actions = p.actions || {}
      return Object.values(actions).some(Boolean)
    })

  const normalizedDatasetFilter = datasetProjectFilter.trim().toLowerCase()
  const filteredDatasetProjects = datasetProjectPerms
    .filter(p => {
      if (!normalizedDatasetFilter) return true
      try {
        return p.project_name.toLowerCase().includes(normalizedDatasetFilter)
      } catch {
        return true
      }
    })
    .filter(p => {
      if (!datasetShowAssignedOnly) return true
      const actions = p.actions || {}
      return Object.values(actions).some(Boolean)
    })

  const normalizedTestDatasetFilter = testDatasetProjectFilter.trim().toLowerCase()
  const filteredTestDatasetProjects = testDatasetProjectPerms
    .filter(p => {
      if (!normalizedTestDatasetFilter) return true
      try {
        return p.project_name.toLowerCase().includes(normalizedTestDatasetFilter)
      } catch {
        return true
      }
    })
    .filter(p => {
      if (!testDatasetShowAssignedOnly) return true
      const actions = p.actions || {}
      return Object.values(actions).some(Boolean)
    })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl h-[80vh] flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-base font-semibold text-gray-900">
              권한 설정 - {user.full_name} ({user.email})
            </div>
            {isSuper && (
              <div className="mt-1 text-xs text-red-600">
                이 사용자는 관리자(superuser)이므로 실제로는 모든 권한을 가지고 있습니다. 체크박스
                설정은 참고용입니다.
              </div>
            )}
          </div>
          <button
            className="text-sm px-3 py-1 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50"
            onClick={onClose}
            disabled={saving}
          >
            닫기
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* 좌측 버티컬 탭 */}
          <aside className="w-56 border-r border-gray-200 bg-gray-50 flex flex-col">
            <div className="px-4 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              권한 카테고리
            </div>
            <nav className="flex-1 px-2 space-y-1 overflow-y-auto">
              {[
                { key: 'global' as const, label: '전역 설정', desc: '페이지 / 기능 접근' },
                { key: 'raw' as const, label: '원천 데이터', desc: '프로젝트별 스코프' },
                { key: 'label' as const, label: '라벨 데이터', desc: '프로젝트별 스코프' },
                { key: 'dataset' as const, label: '학습 데이터셋', desc: '프로젝트별 스코프' },
                { key: 'test-dataset' as const, label: '평가 데이터셋', desc: '프로젝트별 스코프' },
              ].map(tab => {
                const active = activeTab === tab.key
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`w-full text-left rounded-md px-3 py-2 text-sm transition-colors ${
                      active
                        ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <div className="font-medium">{tab.label}</div>
                    <div className="text-[11px] text-gray-500">{tab.desc}</div>
                  </button>
                )
              })}
            </nav>
          </aside>

          {/* 우측 탭 콘텐츠 */}
          <div className="flex-1 min-w-0 overflow-y-auto px-4 py-3">
            {loading ? (
              <div className="text-sm text-gray-500">불러오는 중...</div>
            ) : (
              <>
                {activeTab === 'global' && (
                  <section className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900">전역 설정</h3>
                    <div className="border border-gray-200 rounded-lg bg-gray-50 p-4">
                      {/* 페이지 접근 */}
                      <div>
                        <div className="text-xs font-semibold text-gray-700 mb-1">페이지 접근</div>
                        <p className="text-[11px] text-gray-500 mb-2">
                          사용자가 어떤 페이지 메뉴에 입장할 수 있는지 제어합니다.
                        </p>
                        <div className="space-y-4">
                          {GLOBAL_PERMISSION_GROUPS[0].items.map(item => {
                            if (!allPermissions.includes(item.code)) return null
                            const checked = selectedPerms.has(item.code)
                            return (
                              <label
                                key={item.code}
                                className="flex items-center gap-3 text-xs cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 rounded border-gray-300"
                                  checked={checked}
                                  onChange={() => togglePerm(item.code)}
                                  disabled={saving || isSuper}
                                />
                                <span className="text-gray-800">{item.label}</span>
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  </section>
                )}

                {activeTab === 'raw' && (
                  <section className="space-y-3">
                    {!selectedPerms.has(PERMISSIONS.PAGE_RAW_DATA) && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                        전역 설정에서 <strong>원천 데이터 관리</strong> 페이지 권한을 먼저 활성화하세요.
                      </div>
                    )}
                    <div className={!selectedPerms.has(PERMISSIONS.PAGE_RAW_DATA) ? 'pointer-events-none opacity-40' : ''}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">원천 데이터 권한</h3>
                        <p className="text-[11px] text-gray-500">
                          원천 데이터 프로젝트별로 보기 / 업로드 / 다운로드 / 삭제 / 구조 생성 권한을
                          설정합니다.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="hidden sm:inline-flex rounded border border-gray-200 bg-gray-50">
                          {([
                            { key: 'all', label: '전체' },
                            { key: 'manual', label: '수동' },
                            { key: 'auto', label: '자동' },
                          ] as const).map(item => (
                            <button
                              key={item.key}
                              type="button"
                              className={`px-2 py-1 text-[11px] rounded-sm ${
                                projectUploadModeFilter === item.key
                                  ? 'bg-white text-gray-900 shadow-inner'
                                  : 'text-gray-500 hover:text-gray-900'
                              }`}
                              onClick={() => setProjectUploadModeFilter(item.key)}
                            >
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* 원천 데이터 탭 접근(수동/자동) 전역 권한 */}
                    <div className="mt-2 mb-3 border border-gray-200 rounded-lg bg-gray-50 p-3">
                      <div className="text-[11px] font-semibold text-gray-700 mb-1">
                        원천 데이터 탭 접근
                      </div>
                      <p className="text-[11px] text-gray-500 mb-2">
                        원천 데이터 관리 화면에서 수동/자동 업로드 탭 접근을 제어합니다.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {GLOBAL_PERMISSION_GROUPS[1].items.map(item => {
                          if (!allPermissions.includes(item.code)) return null
                          const checked = selectedPerms.has(item.code)
                          return (
                            <label
                              key={item.code}
                              className="inline-flex items-center gap-1 text-[11px] cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                className="h-3.5 w-3.5 rounded border-gray-300"
                                checked={checked}
                                onChange={() => togglePerm(item.code)}
                                disabled={saving || isSuper}
                              />
                              <span className="text-gray-800">{item.label}</span>
                            </label>
                          )
                        })}
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs mb-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          className="px-2 py-1 border border-gray-300 rounded text-xs"
                          placeholder="프로젝트명 검색"
                          value={projectFilter}
                          onChange={e => setProjectFilter(e.target.value)}
                        />
                        <label className="inline-flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 rounded border-gray-300"
                            checked={rawShowAssignedOnly}
                            onChange={e => setRawShowAssignedOnly(e.target.checked)}
                          />
                          <span className="text-[11px] text-gray-700">권한 있는 프로젝트만</span>
                        </label>
                      </div>
                    </div>

                    {filteredRawProjects.length === 0 ? (
                      <div className="text-xs text-gray-500">
                        프로젝트가 없거나 검색/필터 조건에 해당하는 항목이 없습니다.
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-gray-700 w-1/3">
                                프로젝트
                              </th>
                              <th className="px-2 py-2 text-center font-semibold text-gray-700 text-[11px]">
                                보기
                                <div>
                                  <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 rounded border-gray-300"
                                    checked={filteredRawProjects.every(
                                      p => isRawActionAllowed(p, 'view') && !!(p.actions || {}).view,
                                    )}
                                    onChange={() => toggleRawColumnAll('view', filteredRawProjects)}
                                  />
                                </div>
                              </th>
                              <th className="px-2 py-2 text-center font-semibold text-gray-700 text-[11px]">
                                업로드
                                <div>
                                  <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 rounded border-gray-300"
                                    checked={
                                      filteredRawProjects
                                        .filter(p => isRawActionAllowed(p, 'manual_upload'))
                                        .length > 0 &&
                                      filteredRawProjects
                                        .filter(p => isRawActionAllowed(p, 'manual_upload'))
                                        .every(p => !!(p.actions || {}).manual_upload)
                                    }
                                    onChange={() =>
                                      toggleRawColumnAll('manual_upload', filteredRawProjects)
                                    }
                                  />
                                </div>
                              </th>
                              <th className="px-2 py-2 text-center font-semibold text-gray-700 text-[11px]">
                                다운로드
                                <div>
                                  <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 rounded border-gray-300"
                                    checked={filteredRawProjects.every(
                                      p =>
                                        isRawActionAllowed(p, 'download') &&
                                        !!(p.actions || {}).download,
                                    )}
                                    onChange={() =>
                                      toggleRawColumnAll('download', filteredRawProjects)
                                    }
                                  />
                                </div>
                              </th>
                              <th className="px-2 py-2 text-center font-semibold text-gray-700 text-[11px]">
                                삭제
                                <div>
                                  <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 rounded border-gray-300"
                                    checked={
                                      filteredRawProjects
                                        .filter(p => isRawActionAllowed(p, 'delete'))
                                        .length > 0 &&
                                      filteredRawProjects
                                        .filter(p => isRawActionAllowed(p, 'delete'))
                                        .every(p => !!(p.actions || {}).delete)
                                    }
                                    onChange={() => toggleRawColumnAll('delete', filteredRawProjects)}
                                  />
                                </div>
                              </th>
                              <th className="px-2 py-2 text-center font-semibold text-gray-700 text-[11px]">
                                구조 생성
                                <div>
                                  <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 rounded border-gray-300"
                                    checked={
                                      filteredRawProjects
                                        .filter(p => isRawActionAllowed(p, 'create'))
                                        .length > 0 &&
                                      filteredRawProjects
                                        .filter(p => isRawActionAllowed(p, 'create'))
                                        .every(p => !!(p.actions || {}).create)
                                    }
                                    onChange={() => toggleRawColumnAll('create', filteredRawProjects)}
                                  />
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filteredRawProjects.map(p => {
                              const acts = p.actions || {}
                              const mode = String(p.upload_mode || '').toLowerCase()
                              return (
                                <tr key={p.project_id} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 align-top">
                                    <div className="font-medium text-gray-900 text-xs">
                                      {p.project_name}
                                    </div>
                                    <div className="text-[11px] text-gray-500">
                                      업로드 모드:{' '}
                                      {mode === 'auto' ? '자동 업로드' : '수동 업로드'}
                                    </div>
                                  </td>
                                  {['view', 'manual_upload', 'download', 'delete', 'create'].map(
                                    key => {
                                      const allowed = isRawActionAllowed(p, key)
                                      return (
                                        <td
                                          key={key}
                                          className="px-2 py-2 text-center align-middle"
                                        >
                                          <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-gray-300"
                                            checked={!!acts[key]}
                                            disabled={saving || !allowed}
                                            onChange={() =>
                                              toggleRawProjectAction(p.project_id, key)
                                            }
                                          />
                                        </td>
                                      )
                                    },
                                  )}
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    </div>
                  </section>
                )}

                {activeTab === 'label' && (
                  <section className="space-y-3">
                    {!selectedPerms.has(PERMISSIONS.PAGE_LABEL_DATA) && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                        전역 설정에서 <strong>라벨 데이터 관리</strong> 페이지 권한을 먼저 활성화하세요.
                      </div>
                    )}
                    <div className={!selectedPerms.has(PERMISSIONS.PAGE_LABEL_DATA) ? 'pointer-events-none opacity-40' : ''}>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">라벨 데이터 권한</h3>
                      <p className="text-[11px] text-gray-500">
                        라벨 프로젝트별로 보기 / 업로드 / 다운로드 / 구조 생성 권한을 설정합니다.
                      </p>
                    </div>

                    <div className="flex items-center justify-between text-xs mb-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          className="px-2 py-1 border border-gray-300 rounded text-xs"
                          placeholder="라벨 프로젝트명 검색"
                          value={labelProjectFilter}
                          onChange={e => setLabelProjectFilter(e.target.value)}
                        />
                        <label className="inline-flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 rounded border-gray-300"
                            checked={labelShowAssignedOnly}
                            onChange={e => setLabelShowAssignedOnly(e.target.checked)}
                          />
                          <span className="text-[11px] text-gray-700">권한 있는 프로젝트만</span>
                        </label>
                      </div>
                    </div>

                    {filteredLabelProjects.length === 0 ? (
                      <div className="text-xs text-gray-500">
                        라벨 프로젝트가 없거나 검색/필터 조건에 해당하는 항목이 없습니다.
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-gray-700 w-1/3">
                                프로젝트
                              </th>
                              {['view', 'upload', 'download', 'create'].map(key => (
                                <th
                                  key={key}
                                  className="px-2 py-2 text-center font-semibold text-gray-700 text-[11px]"
                                >
                                  {key === 'view'
                                    ? '보기'
                                    : key === 'upload'
                                    ? '업로드/관리'
                                    : key === 'download'
                                    ? '다운로드'
                                    : '구조 생성'}
                                  <div>
                                    <input
                                      type="checkbox"
                                      className="h-3.5 w-3.5 rounded border-gray-300"
                                      checked={filteredLabelProjects.every(
                                        p => !!(p.actions || {})[key],
                                      )}
                                      onChange={() =>
                                        toggleLabelColumnAll(key, filteredLabelProjects)
                                      }
                                    />
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filteredLabelProjects.map(p => {
                              const acts = p.actions || {}
                              return (
                                <tr key={p.project_id} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 align-top">
                                    <div className="font-medium text-gray-900 text-xs">
                                      {p.project_name}
                                    </div>
                                  </td>
                                  {['view', 'upload', 'download', 'create'].map(key => (
                                    <td
                                      key={key}
                                      className="px-2 py-2 text-center align-middle"
                                    >
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300"
                                        checked={!!acts[key]}
                                        disabled={saving}
                                        onChange={() =>
                                          toggleLabelProjectAction(p.project_id, key)
                                        }
                                      />
                                    </td>
                                  ))}
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    </div>
                  </section>
                )}

                {activeTab === 'test-dataset' && (
                  <section className="space-y-3">
                    {!selectedPerms.has(PERMISSIONS.PAGE_TEST_DATASET) && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                        전역 설정에서 <strong>평가 데이터셋 관리</strong> 페이지 권한을 먼저 활성화하세요.
                      </div>
                    )}
                    <div className={!selectedPerms.has(PERMISSIONS.PAGE_TEST_DATASET) ? 'pointer-events-none opacity-40' : ''}>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">평가 데이터셋 권한</h3>
                      <p className="text-[11px] text-gray-500">
                        평가 데이터셋 프로젝트별로 보기 / 빌드 / 다운로드 / 삭제 / 구조 생성 권한을 설정합니다.
                      </p>
                    </div>

                    <div className="flex items-center gap-2 text-xs mb-2">
                      <input
                        type="text"
                        className="px-2 py-1 border border-gray-300 rounded text-xs"
                        placeholder="프로젝트명 검색"
                        value={testDatasetProjectFilter}
                        onChange={e => setTestDatasetProjectFilter(e.target.value)}
                      />
                      <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 rounded border-gray-300"
                          checked={testDatasetShowAssignedOnly}
                          onChange={e => setTestDatasetShowAssignedOnly(e.target.checked)}
                        />
                        <span className="text-[11px] text-gray-700">권한 있는 프로젝트만</span>
                      </label>
                    </div>

                    {filteredTestDatasetProjects.length === 0 ? (
                      <div className="text-xs text-gray-500">
                        평가 데이터셋 프로젝트가 없거나 검색/필터 조건에 해당하는 항목이 없습니다.
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-gray-700 w-1/3">
                                프로젝트
                              </th>
                              {['view', 'build', 'download', 'delete', 'create'].map(key => (
                                <th
                                  key={key}
                                  className="px-2 py-2 text-center font-semibold text-gray-700 text-[11px]"
                                >
                                  {key === 'view' ? '보기' : key === 'build' ? '빌드' : key === 'download' ? '다운로드' : key === 'delete' ? '삭제' : '구조 생성'}
                                  <div>
                                    <input
                                      type="checkbox"
                                      className="h-3.5 w-3.5 rounded border-gray-300"
                                      checked={filteredTestDatasetProjects.every(
                                        p => !!(p.actions || {})[key],
                                      )}
                                      onChange={() =>
                                        toggleTestDatasetColumnAll(key, filteredTestDatasetProjects)
                                      }
                                    />
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filteredTestDatasetProjects.map(p => {
                              const acts = p.actions || {}
                              return (
                                <tr key={p.project_id} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 align-top">
                                    <div className="font-medium text-gray-900 text-xs">
                                      {p.project_name}
                                    </div>
                                  </td>
                                  {['view', 'build', 'download', 'delete', 'create'].map(key => (
                                    <td
                                      key={key}
                                      className="px-2 py-2 text-center align-middle"
                                    >
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300"
                                        checked={!!acts[key]}
                                        disabled={saving}
                                        onChange={() =>
                                          toggleTestDatasetProjectAction(p.project_id, key)
                                        }
                                      />
                                    </td>
                                  ))}
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    </div>
                  </section>
                )}

                {activeTab === 'dataset' && (
                  <section className="space-y-3">
                    {!selectedPerms.has(PERMISSIONS.PAGE_DATASET) && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                        전역 설정에서 <strong>학습 데이터셋 관리</strong> 페이지 권한을 먼저 활성화하세요.
                      </div>
                    )}
                    <div className={!selectedPerms.has(PERMISSIONS.PAGE_DATASET) ? 'pointer-events-none opacity-40' : ''}>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">학습 데이터셋 권한</h3>
                      <p className="text-[11px] text-gray-500">
                        학습 데이터셋 프로젝트별로 보기 / 빌드 / 다운로드 / 삭제 / 구조 생성 권한을
                        설정합니다.
                      </p>
                    </div>

                    <div className="flex items-center justify-between text-xs mb-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          className="px-2 py-1 border border-gray-300 rounded text-xs"
                          placeholder="데이터셋 프로젝트명 검색"
                          value={datasetProjectFilter}
                          onChange={e => setDatasetProjectFilter(e.target.value)}
                        />
                        <label className="inline-flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 rounded border-gray-300"
                            checked={datasetShowAssignedOnly}
                            onChange={e => setDatasetShowAssignedOnly(e.target.checked)}
                          />
                          <span className="text-[11px] text-gray-700">권한 있는 프로젝트만</span>
                        </label>
                      </div>
                    </div>

                    {filteredDatasetProjects.length === 0 ? (
                      <div className="text-xs text-gray-500">
                        데이터셋 프로젝트가 없거나 검색/필터 조건에 해당하는 항목이 없습니다.
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-gray-700 w-1/3">
                                프로젝트
                              </th>
                              {['view', 'build', 'download', 'delete', 'create'].map(key => (
                                <th
                                  key={key}
                                  className="px-2 py-2 text-center font-semibold text-gray-700 text-[11px]"
                                >
                                  {key === 'view'
                                    ? '보기'
                                    : key === 'build'
                                    ? '빌드'
                                    : key === 'download'
                                    ? '다운로드'
                                    : key === 'delete'
                                    ? '삭제'
                                    : '구조 생성'}
                                  <div>
                                    <input
                                      type="checkbox"
                                      className="h-3.5 w-3.5 rounded border-gray-300"
                                      checked={filteredDatasetProjects.every(
                                        p => !!(p.actions || {})[key],
                                      )}
                                      onChange={() =>
                                        toggleDatasetColumnAll(key, filteredDatasetProjects)
                                      }
                                    />
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filteredDatasetProjects.map(p => {
                              const acts = p.actions || {}
                              return (
                                <tr key={p.project_id} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 align-top">
                                    <div className="font-medium text-gray-900 text-xs">
                                      {p.project_name}
                                    </div>
                                  </td>
                                  {['view', 'build', 'download', 'delete', 'create'].map(
                                    key => (
                                      <td
                                        key={key}
                                        className="px-2 py-2 text-center align-middle"
                                      >
                                        <input
                                          type="checkbox"
                                          className="h-4 w-4 rounded border-gray-300"
                                          checked={!!acts[key]}
                                          disabled={saving}
                                          onChange={() =>
                                            toggleDatasetProjectAction(p.project_id, key)
                                          }
                                        />
                                      </td>
                                    ),
                                  )}
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    </div>
                  </section>
                )}
              </>
            )}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            className="text-sm px-3 py-1 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50"
            onClick={onClose}
            disabled={saving}
          >
            취소
          </button>
          <button
            className="text-sm px-3 py-1 rounded text-white bg-gray-900 hover:bg-gray-950 disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  )
}
